/**
 * Shared internals of the tiered relevance search.
 *
 * The per-query pipeline is split into four composable steps so that BOTH
 * entry points run the exact same code:
 *   - `tieredSearch` (sync, one shot — `./tieredSearch.ts`)
 *   - `createTimeSlicedSearchIndex` (async, chunked with main-thread yields —
 *     `./timeSlicedSearch.ts`, STK-1399)
 *
 * Steps:
 *   1. buildQueryVariants   — parse + number-normalize the query (query-only)
 *   2. classifyPreparedItems — deterministic tiers 0-5, one pass per item
 *   3. collectFuseScores     — tier-6 fuzzy scores via a Fuse index
 *   4. finalizeTieredResults — tier-6 membership, sorts, result wrapping
 *
 * Steps 2 and 3 are accumulative (they mutate caller-owned buckets / score
 * maps), which is what makes chunking possible: run them per slice of the
 * corpus, then finalize once. A Fuse score depends only on (query, item) —
 * never on the rest of the corpus — so per-chunk indexes yield the same
 * scores as one monolithic index.
 */
import Fuse, { type IFuseOptions } from "fuse.js"
import { normalizeNumbersInQuery } from "./numberNormalization"

/**
 * Fuse.js (v7) throws "Pattern length exceeds max of 32." for patterns longer
 * than the Bitap word size. The deterministic tiers (0-4) don't have this
 * limit, so we truncate only the pattern handed to Fuse and leave the original
 * query for exact/prefix/substring matching. 31 stays one char below Fuse's
 * strict `>` check to leave a margin for any silent off-by-one inside the
 * library.
 */
const FUSE_MAX_PATTERN_LENGTH = 31

function trimForFuse(pattern: string): string {
  return pattern.length > FUSE_MAX_PATTERN_LENGTH
    ? pattern.slice(0, FUSE_MAX_PATTERN_LENGTH)
    : pattern
}

export type TieredMatch =
  | "exact-code"
  | "exact-field"
  | "code-prefix"
  | "word-prefix"
  | "substring"
  | "token-match"
  | "fuzzy"

export interface TieredSearchResult<T> {
  item: T
  tier: TieredMatch
  /** 0 = exact-code … 6 = fuzzy. Matches `tier` 1:1; provided for callers that want a numeric handle. */
  tierIndex: number
  /** Best fuse score across query variants (lower = closer). undefined when Fuse never matched the item. */
  fuseScore?: number
}

export interface TieredSearchConfig<T> {
  /** Extract code field for exact/prefix match (tiers 0, 2). */
  codeField?: (item: T) => string | null | undefined
  /** Extract all text fields for deterministic matching (tiers 1, 3-5). */
  textFields: (item: T) => Array<string | null | undefined>
  /** Fuse.js options — used for tier 6 AND secondary sort within all tiers. Must include `includeScore: true`. */
  fuseOptions: IFuseOptions<T>
  /** Expand query with number↔word variants (e.g. "three"↔"3"). Default true. */
  normalizeNumbers?: boolean
}

const SEPARATOR_RE = /[-_/\s.]/g
const stripSeparators = (s: string) => s.replace(SEPARATOR_RE, "")

/**
 * Punctuation separators whose role is context-dependent: `, . - _ /`. Between
 * two digits they bind a size grade; anywhere else they are word boundaries.
 * Whitespace is NOT in this class — it is always an unambiguous word boundary.
 */
const PUNCT_SEP = "[,.\\-_/]"

/**
 * Tokenize for word/token matching (tiers 3-5), treating a numeric SIZE GRADE
 * as one comparable unit. Plain whitespace splitting shreds size grades and lets
 * trivial wording differences (e.g. "NORV" vs "NORVEGE") decide a match instead
 * of the size — the actual distinguishing attribute on fish/seafood SKUs.
 *
 * One rule, applied uniformly to every punctuation separator (no per-character
 * special cases):
 *
 *   Punctuation BETWEEN TWO DIGITS binds a size grade — normalize it to a single
 *   "/" and keep the token whole. Whitespace, and any punctuation NOT between
 *   two digits, is a word boundary — split on it.
 *
 * So all notations of the same grade collapse together ("1,5/2" = "1.5/2" =
 * "40-60" ≡ "40/60") while a grade stays DISTINCT from the reversed range
 * ("1,5/2" → "1/5/2" ≠ "1/1,5" → "1/1/5"). A trailing kilo unit is dropped so
 * "2k" ≡ "2". Punctuation next to a non-digit still splits ("NORV.1,5" → "norv"
 * + "1/5", "s/v" → "s" + "v"); a space between digits splits too ("1,5 2K" →
 * "1/5" + "2").
 *
 * Applied to BOTH the query and the target so the AI search path and the
 * frontend pickers (which share this core) tokenize identically.
 */
const GRADE_SEP_RE = new RegExp(`(\\d)${PUNCT_SEP}+(?=\\d)`, "g")
const KILO_UNIT_RE = /(\d)k(?:gs?)?\b/g
const WORD_BOUNDARY_RE = new RegExp(
  `\\s+|(?<!\\d)${PUNCT_SEP}|${PUNCT_SEP}(?!\\d)`,
  "g"
)

export function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(GRADE_SEP_RE, "$1/")
    .replace(KILO_UNIT_RE, "$1")
    .split(WORD_BOUNDARY_RE)
    .filter(Boolean)
}

/**
 * Token-match scoring: +2 per query word equaling a target word,
 * +1 per query word that is a prefix of a target word (min 2 chars).
 * Exported because the frontend `tokenMatchFilter` reuses the same scale.
 */
export function tokenMatchScore(
  queryWords: string[],
  targetWords: string[]
): number {
  let score = 0
  for (const qw of queryWords) {
    if (targetWords.some((tw) => tw === qw)) {
      score += 2
    } else if (qw.length >= 2 && targetWords.some((tw) => tw.startsWith(qw))) {
      score += 1
    }
  }
  return score
}

/**
 * Per-item derived strings the tiered scan needs: lowercased code + its
 * separator-stripped form, lowercased text fields, and the tokenized target
 * words + their joined text. These depend ONLY on the item, never on the
 * query — so computing them once per corpus and passing them to
 * `tieredSearch` as `prepared` avoids re-tokenizing every item on every
 * keystroke, the dominant cost when searching a large catalog (35k+ rows).
 */
export interface PreparedItem<T> {
  item: T
  code: string | null
  codeStripped: string | null
  fieldsLower: string[]
  targetWords: string[]
  text: string
}

/**
 * Precompute {@link PreparedItem}s for a corpus. Call once per item list
 * (memoize on the array identity) and hand the result to `tieredSearch`
 * via its `prepared` argument. Produces exactly the same per-item derivations
 * the inline scan would, so results are identical — only cheaper.
 */
export function prepareItems<T>(
  items: ReadonlyArray<T>,
  config: Pick<TieredSearchConfig<T>, "codeField" | "textFields">
): PreparedItem<T>[] {
  const prepared: PreparedItem<T>[] = new Array(items.length)
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const code = config.codeField?.(item)?.toLowerCase() ?? null
    const fieldsLower = config
      .textFields(item)
      .filter((f): f is string => f != null)
      .map((f) => f.toLowerCase())
    const targetWords = tokenize(fieldsLower.join(" "))
    prepared[i] = {
      item,
      code,
      codeStripped: code ? stripSeparators(code) : null,
      fieldsLower,
      targetWords,
      text: targetWords.join(" "),
    }
  }
  return prepared
}

// ── Step 1: query variants ──────────────────────────────────────────

export interface QueryVariant {
  raw: string
  lower: string
  lowerStripped: string
  words: string[]
}

const MAX_VARIANTS = 4

/** Parse a (trimmed, non-empty) query into its number-normalized variants. */
export function buildQueryVariants(
  q: string,
  normalizeNumbers: boolean
): QueryVariant[] {
  const allVariants = normalizeNumbers ? normalizeNumbersInQuery(q) : [q]
  const queryVariants =
    allVariants.length > MAX_VARIANTS
      ? allVariants.slice(0, MAX_VARIANTS)
      : allVariants
  return queryVariants.map((v) => {
    const lower = v.toLowerCase()
    return {
      raw: v,
      lower,
      lowerStripped: stripSeparators(lower),
      words: tokenize(v),
    }
  })
}

// ── Step 2: deterministic classification (tiers 0-5) ────────────────

export interface TierBuckets<T> {
  tier0: T[]
  tier1: T[]
  tier2: T[]
  tier3: T[]
  tier4: T[]
  tier5scored: Array<{ item: T; score: number; originalScore: number }>
  /**
   * Tiers 1-4 only: items that matched via the ORIGINAL query
   * (variantData[0]) outrank items that only matched after number
   * normalization, before falling back to Fuse score / tiebreaker.
   */
  matchedOriginal: Set<T>
}

export function createTierBuckets<T>(): TierBuckets<T> {
  return {
    tier0: [],
    tier1: [],
    tier2: [],
    tier3: [],
    tier4: [],
    tier5scored: [],
    matchedOriginal: new Set<T>(),
  }
}

/**
 * Classify a slice of prepared items into the deterministic tiers (0-5),
 * accumulating into caller-owned buckets. Pure per-item — safe to call once
 * per corpus chunk; the union of chunked calls equals one whole-corpus call.
 */
export function classifyPreparedItems<T>(
  prep: ReadonlyArray<PreparedItem<T>>,
  variantData: ReadonlyArray<QueryVariant>,
  buckets: TierBuckets<T>
): void {
  for (const p of prep) {
    const { item, code, codeStripped, fieldsLower, targetWords, text } = p

    if (
      code &&
      variantData.some(
        (v) => code === v.lower || codeStripped === v.lowerStripped
      )
    ) {
      buckets.tier0.push(item)
      continue
    }

    let matched = false
    for (let vi = 0; vi < variantData.length; vi++) {
      const v = variantData[vi]
      if (fieldsLower.some((f) => f === v.lower)) {
        buckets.tier1.push(item)
        if (vi === 0) buckets.matchedOriginal.add(item)
        matched = true
        break
      }
    }
    if (matched) continue

    if (code) {
      for (let vi = 0; vi < variantData.length; vi++) {
        const v = variantData[vi]
        if (
          code.startsWith(v.lower) ||
          (codeStripped &&
            v.lowerStripped &&
            codeStripped.startsWith(v.lowerStripped))
        ) {
          buckets.tier2.push(item)
          if (vi === 0) buckets.matchedOriginal.add(item)
          matched = true
          break
        }
      }
      if (matched) continue
    }

    // Tier 3/4 use the prepared `targetWords` / `text`.
    // NOTE: tier 4 is the loosest deterministic tier — a narrow-grade query
    // token ("1/5") can substring-match a wider grade ("1/5/2"). This is
    // intentional (tier 4 is "contains"-level recall, ranked below the exact
    // tiers 0-3), not a regression.
    for (let vi = 0; vi < variantData.length; vi++) {
      const v = variantData[vi]
      if (
        v.words.length > 0 &&
        v.words.every((qw) => targetWords.some((tw) => tw.startsWith(qw)))
      ) {
        buckets.tier3.push(item)
        if (vi === 0) buckets.matchedOriginal.add(item)
        matched = true
        break
      }
    }
    if (matched) continue

    for (let vi = 0; vi < variantData.length; vi++) {
      const v = variantData[vi]
      if (v.words.length > 0 && v.words.every((qw) => text.includes(qw))) {
        buckets.tier4.push(item)
        if (vi === 0) buckets.matchedOriginal.add(item)
        matched = true
        break
      }
    }
    if (matched) continue

    let bestScore = 0
    let originalScore = 0
    for (let vi = 0; vi < variantData.length; vi++) {
      const v = variantData[vi]
      if (v.words.length > 0) {
        const score = tokenMatchScore(v.words, targetWords)
        if (score > bestScore) bestScore = score
        if (vi === 0) originalScore = score
      }
    }
    if (bestScore > 0) {
      buckets.tier5scored.push({ item, score: bestScore, originalScore })
      continue
    }
  }
}

// ── Step 3: fuzzy scores (tier 6 + secondary sort) ──────────────────

/**
 * Fuse pass: per-item score map, used both as secondary sort within tiers 0-5
 * AND to populate tier 6 (items Fuse matched that no deterministic tier caught).
 * Multi-word queries search EACH word and average per-item scores so a query
 * like "viv vand" can match "VIVIERS DE VENDEE" (non-contiguous) — Fuse alone
 * searches contiguously and would whiff.
 *
 * Accumulates into a caller-owned map. A Fuse score depends only on
 * (pattern, item) — never on the rest of the corpus — so calling this once
 * per chunk-local index yields the same map as one whole-corpus index.
 */
export function collectFuseScores<T>(
  fuse: Fuse<T>,
  variantData: ReadonlyArray<QueryVariant>,
  fuseScoreMap: Map<T, number>
): void {
  for (const v of variantData) {
    if (v.words.length <= 1) {
      for (const r of fuse.search(trimForFuse(v.raw))) {
        const existing = fuseScoreMap.get(r.item)
        const score = r.score ?? 1
        if (existing === undefined || score < existing) {
          fuseScoreMap.set(r.item, score)
        }
      }
    } else {
      const perItemWordScores = new Map<T, number[]>()
      for (let wi = 0; wi < v.words.length; wi++) {
        for (const r of fuse.search(trimForFuse(v.words[wi]))) {
          let arr = perItemWordScores.get(r.item)
          if (!arr) {
            arr = new Array(v.words.length).fill(1)
            perItemWordScores.set(r.item, arr)
          }
          const score = r.score ?? 1
          if (score < arr[wi]) {
            arr[wi] = score
          }
        }
      }
      perItemWordScores.forEach((scores, item) => {
        const avg =
          scores.reduce((a: number, b: number) => a + b, 0) / scores.length
        const existing = fuseScoreMap.get(item)
        if (existing === undefined || avg < existing) {
          fuseScoreMap.set(item, avg)
        }
      })
    }
  }
}

// ── Step 4: tier-6 membership, sorts, wrapping ──────────────────────

/**
 * Assemble the final ranked result list from accumulated buckets + scores.
 * `tier6Filter`, when provided, restricts fuzzy-tier membership to a known
 * item set — used by the sync path to guard against a stale `prebuiltFuse`
 * cached over a larger corpus than the current `items`.
 */
export function finalizeTieredResults<T>(
  buckets: TierBuckets<T>,
  fuseScoreMap: Map<T, number>,
  tiebreaker?: (a: T, b: T) => number,
  tier6Filter?: Set<T> | null
): TieredSearchResult<T>[] {
  const { tier0, tier1, tier2, tier3, tier4, tier5scored, matchedOriginal } =
    buckets

  const matchedSet = new Set<T>([
    ...tier0,
    ...tier1,
    ...tier2,
    ...tier3,
    ...tier4,
    ...tier5scored.map((e) => e.item),
  ])
  const tier6: T[] = []
  fuseScoreMap.forEach((_score, item) => {
    if (!matchedSet.has(item) && (!tier6Filter || tier6Filter.has(item))) {
      tier6.push(item)
    }
  })

  const fuseSort = (a: T, b: T): number => {
    const sa = fuseScoreMap.get(a) ?? 1
    const sb = fuseScoreMap.get(b) ?? 1
    return sa - sb
  }
  const combinedSort = (a: T, b: T): number => {
    const fuseDiff = fuseSort(a, b)
    if (fuseDiff !== 0) return fuseDiff
    return tiebreaker ? tiebreaker(a, b) : 0
  }
  const originalFirstSort = (a: T, b: T): number => {
    const ao = matchedOriginal.has(a) ? 0 : 1
    const bo = matchedOriginal.has(b) ? 0 : 1
    if (ao !== bo) return ao - bo
    return combinedSort(a, b)
  }

  tier0.sort(combinedSort)
  tier1.sort(originalFirstSort)
  tier2.sort(originalFirstSort)
  tier3.sort(originalFirstSort)
  tier4.sort(originalFirstSort)
  tier6.sort(combinedSort)

  tier5scored.sort((a, b) => {
    const scoreDiff = b.score - a.score
    if (scoreDiff !== 0) return scoreDiff
    const aOriginalBest = a.originalScore === a.score ? 0 : 1
    const bOriginalBest = b.originalScore === b.score ? 0 : 1
    if (aOriginalBest !== bOriginalBest) return aOriginalBest - bOriginalBest
    return combinedSort(a.item, b.item)
  })

  const wrap = (
    arr: T[],
    tier: TieredMatch,
    tierIndex: number
  ): TieredSearchResult<T>[] =>
    arr.map((item) => ({
      item,
      tier,
      tierIndex,
      fuseScore: fuseScoreMap.get(item),
    }))

  return [
    ...wrap(tier0, "exact-code", 0),
    ...wrap(tier1, "exact-field", 1),
    ...wrap(tier2, "code-prefix", 2),
    ...wrap(tier3, "word-prefix", 3),
    ...wrap(tier4, "substring", 4),
    ...tier5scored.map((e) => ({
      item: e.item,
      tier: "token-match" as const,
      tierIndex: 5,
      fuseScore: fuseScoreMap.get(e.item),
    })),
    ...wrap(tier6, "fuzzy", 6),
  ]
}
